<template>
  <div
    class="d-flex flex-column justify-content-between position-relative position-sm-abs"
    style="top:0;bottom:0;left:0;right:0;"
  >
    <b-row class="flex-fill overflow-hidden">
      <b-col
        lg="3"
        sm="9"
        cols="12"
        class="h-auto h-sm-100"
      >
        <job-sidebar
          :general-tab="generalTab"
          :output-tabs="outputTabs"
          :input-tabs="inputTabs"
          :active-tab="activeTab"
          :active-child-index="activeChildIndex"
          disable-add
          @selected="tabSelected"
          @childSelected="childTabSelected"
        />
      </b-col>
      <b-col
        lg="9"
        cols="12"
        class="h-auto h-sm-100"
      >
        <div
          class="mh-auto mh-sm-100"
          style="
            overflow-y:auto;
            overflow-x:hidden;
            "
        >
          <paper>
            <b-row
              class="mt-n2 px-2 py-1 justify-content-between align-items-center"
            >
              <b-col>
                {{ jobTemplate.name }}
              </b-col>
              <b-col class="d-flex justify-content-end">
                <b-button
                  variant="bg-white"
                  class="mr-1"
                  :disabled="!isEditable"
                  @click="
                    isEditable
                      ? $router.push({
                        name: 'job-templates-edit',
                        params: {
                          id: jobTemplate.id,
                          activeTab: {
                            childTabIndex: activeChildIndex,
                            tab: activeTab,
                          },
                        },
                      })
                      : ''
                  "
                >
                  <GjIcon
                    name="Edit"
                    class="icon-left"
                  />
                  Edit
                </b-button>
                <b-button
                  variant="bg-white"
                  :disabled="!isEditable"
                  @click="() => (isEditable ? deleteJobTemplate() : '')"
                >
                  <GjIcon
                    name="Delete"
                    class="icon-left"
                  />
                  Delete
                </b-button>
              </b-col>
            </b-row>
          </paper>
          <general-settings
            v-show="activeTab.id === generalTab.id"
            :job-template="jobTemplate"
            @openInfo="openInfo"
          />
          <template v-for="tab in tabs">
            <div
              v-show="activeTab.id === tab.id"
              :key="tab.id"
            >
              <output-settings
                v-if="isOutput(tab)"
                ref="outputSettings"
                :tab="tab"
                :active="activeTab.id"
                :edit="edit"
                :editing-output-index="activeChildIndex"
                @editingOutputIndex="initEditingOutput"
                @openInfo="openInfo"
              />
              <input-settings
                v-else
                :tab="tab"
              />
            </div>
          </template>
        </div>
      </b-col>
    </b-row>
    <info
      v-if="infoData"
      :info="infoData"
      @close="infoData = null"
    />
  </div>
</template>

<script>
import { SidebarTabType } from '@/helpers/encoder/job-template-sidebar-enum'
import Paper from '@/components/paper/Paper.vue'
import GeneralSettings from './settings/GeneralSettings.vue'
import JobSidebar from '../job-sidebar/JobSidebar.vue'
import InputSettings from './settings/InputSettings.vue'
import OutputSettings from './settings/OutputSettings.vue'

const generalSettingsTab = {
  name: 'General Settings',
  id: '0',
  type: SidebarTabType.GENERAL,
}

export default {
  name: 'JobTemplateDetails',
  components: {
    GeneralSettings,
    JobSidebar,
    InputSettings,
    OutputSettings,
    Paper,
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      infoData: null,
      fetchingGroupSettings: false,
      jobTemplate: null,
      outputGroupModal: false,
      modalOutputGroupRadioSelect: null,
      editingOutputIndex: null,
      generalTab: generalSettingsTab,
      activeTab: generalSettingsTab,
      activeChildIndex: null,
      inputTabs: [
        {
          name: 'Input 1',
          id: '1',
          type: SidebarTabType.INPUT,
        },
      ],
      outputTabs: [],
      outputTabId: 0,
    }
  },
  computed: {
    isEditable() {
      return (
        this.jobTemplate.jobTemplateType === 1
        || this.jobTemplate.jobTemplateType === 3
      )
    },
    tabs() {
      return [...this.inputTabs, ...this.outputTabs]
    },
    outputGroupOptions() {
      return this.$store.state.jobTemplates.outputGroupOptions
    },
    isInvalidManualCheck() {
      return this.outputTabs.length === 0
    },
    outputGroupsAreInvalid() {
      return !!this.outputTabs.find(og => {
        if (og.outputGroup.outputs.length === 0) {
          return true
        }
        if (og.outputGroup.outputs.find(o => o.presetId === null)) {
          return true
        }
        if (
          og.outputGroup.outputs.length > 1
          && og.outputGroup.outputs.some(o => !o.nameModifier)
        ) {
          return true
        }

        const names = og.outputGroup.outputs.map(o => o.nameModifier)

        if (names.length !== [...new Set(names)].length) {
          return true
        }

        return false
      })
    },
  },
  created() {
    this.init()
  },
  methods: {
    deleteJobTemplate() {
      this.confirmDelete(
        'Delete job template',
        'Are you sure you want to delete this job template?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('jobTemplates/deleteJobTemplate', this.jobTemplate.id)
            .then(() => {
              this.$router.push({
                name: 'job-templates',
                params: {
                  projectId: this.$route.params.projectId,
                  toast: {
                    title: 'Success',
                    variant: 'success',
                    message: 'Job template deleted.',
                  },
                },
              })
            })
            .catch(() => {
              this.errorToast('An error occurred', 'Error')
            })
        }
      })
    },
    initEditingOutput(i) {
      this.activeChildIndex = i
      this.editingOutputIndex = i
    },
    init() {
      this.jobTemplate = this.$store.state.jobTemplates.jobTemplate
      this.outputTabs = this.jobTemplate.groups.map(gr => this.createOutputTabObj(gr))
    },
    isOutput(tab) {
      return tab.type === SidebarTabType.OUTPUT
    },
    createOutputTabObj(outputGroupElement) {
      return {
        name: outputGroupElement.name,
        id: `group${this.outputTabId++}`,
        type: SidebarTabType.OUTPUT,
        outputGroupId: outputGroupElement.id,
        outputGroup: outputGroupElement,
        customName: outputGroupElement.customName,
        childTabs: outputGroupElement.outputs,
      }
    },
    openInfo(e) {
      this.infoData = e
    },
    tabSelected(tab) {
      this.editingOutputIndex = null
      this.activeChildIndex = null
      this.activeTab = tab
    },
    childTabSelected(e) {
      this.editingOutputIndex = e.childTabIndex
      this.activeChildIndex = e.childTabIndex
      this.activeTab = e.tab
    },
  },
}
</script>
<style lang="scss" scoped>
.modalLoading {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
.blurForm {
  filter: blur(1px);
}
</style>
